import { motion, AnimatePresence } from 'framer-motion';

function ThoughtBubbleSvg() {
  return (
    <motion.svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className="shrink-0"
      animate={{ scale: [1, 1.15, 1], opacity: [0.45, 0.65, 0.45] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      <ellipse cx="12" cy="10" rx="9" ry="7" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity={0.08} />
      <circle cx="7" cy="19" r="1.5" fill="currentColor" fillOpacity={0.3} />
      <circle cx="4.5" cy="22" r="1" fill="currentColor" fillOpacity={0.2} />
    </motion.svg>
  );
}
import { useState, useEffect, useRef } from 'react';
import { PHRASE_LIBRARY, getEventOverrideLine } from '@/config/buddyPhraseLibrary';

interface BuddyThoughtTickerProps {
  mood: string;
  weather: string;
  networkState: string;
  blockState: string;
  priceState: string;
  verdict: string;
  sentimentBucket?: string | null;
  activeSignature?: string | null;
  blocksToHalving?: number;
  hashrateChange7d?: number;
  lightningVitalityScore?: number;
  footerLine?: string;
  macroState?: string;
  seasonState?: string;
}

export default function BuddyThoughtTicker({
  mood, networkState, blockState,
  sentimentBucket, activeSignature, blocksToHalving, hashrateChange7d, lightningVitalityScore,
  footerLine, macroState, seasonState,
}: BuddyThoughtTickerProps) {
  const [currentThought, setCurrentThought] = useState('');
  const indexRef = useRef(0);
  const poolRef = useRef<string[]>([]);

  useEffect(() => {
    const pool: string[] = [];

    // Priority 1: Event override phrases (high weight)
    const blockFound = blockState === 'fresh';
    const mempoolJam = networkState === 'congested' || blockState === 'drought';

    const eventLine = getEventOverrideLine({
      blockJustFound: blockFound,
      isMempoolJam: mempoolJam,
      seed: Date.now(),
    });

    if (eventLine) {
      // Add event pool with high weight
      const eventPool = blockFound ? PHRASE_LIBRARY.BLOCK_FOUND : PHRASE_LIBRARY.MEMPOOL_JAM;
      pool.push(...eventPool);
    }

    // Priority 2: Macro state phrases
    const macroKey = macroState as keyof typeof PHRASE_LIBRARY;
    if (macroKey && PHRASE_LIBRARY[macroKey]) {
      pool.push(...PHRASE_LIBRARY[macroKey]);
    } else {
      pool.push(...PHRASE_LIBRARY.NEUTRAL);
    }

    // Priority 3: Season flavor (partial blend)
    if (seasonState === 'cold' || seasonState === 'WINTER') {
      // Add ~4 winter phrases for flavor
      const winter = PHRASE_LIBRARY.WINTER;
      pool.push(winter[0], winter[1], winter[2], winter[3]);
    } else if (seasonState === 'heat' || seasonState === 'HEAT' || seasonState === 'SUMMER') {
      const summer = PHRASE_LIBRARY.SUMMER;
      pool.push(summer[0], summer[1], summer[2], summer[3]);
    }

    // Sentiment flavor
    if (sentimentBucket === 'extreme_fear' || sentimentBucket === 'fear') {
      pool.push(...PHRASE_LIBRARY.DEEP_VALUE.slice(0, 4));
    } else if (sentimentBucket === 'extreme_greed' || sentimentBucket === 'greed') {
      pool.push(...PHRASE_LIBRARY.HOT.slice(0, 4));
    }

    // Halving proximity
    if (blocksToHalving != null && blocksToHalving < 2016) {
      pool.push(`${blocksToHalving.toLocaleString()} blocks to the next halving`);
    }

    // Hashrate flavor
    if (hashrateChange7d != null && hashrateChange7d > 5) {
      pool.push("Hashrate climbing. Fortress mode.");
    }
    if (hashrateChange7d != null && hashrateChange7d < -5) {
      pool.push("Miners going quiet.");
    }

    // Lightning flavor
    if (lightningVitalityScore != null && lightningVitalityScore > 0.7) {
      pool.push("Lightning lanes open.");
    }

    // Active signature phrases — enhanced for visibility
    if (activeSignature) {
      const sigPhrases: Record<string, string[]> = {
        queueStare: [
          'Waiting professionally.',
          'Queue spiritually unresolved.',
          'Still no block.',
          'The mempool has opinions.',
          'Watching. Quietly.',
        ],
        minerPrayer: ['Praying to the hash gods.', 'Miners, please.'],
        reliefExhale: [
          'Morale improved slightly.',
          'Block found. Breathing resumes.',
          'The chain exhales.',
          'That pressure had to break.',
          'A good block.',
        ],
        crabDeadpan: ['Sideways. Again.', 'The crab continues.'],
        laserFlex: ['Conviction mode.', 'Feeling it.'],
        feeFeverTwitch: ['These fees though.', 'Fee fever.'],
      };
      if (sigPhrases[activeSignature]) {
        // Give signature phrases very high weight so they show during the reaction
        const phrases = sigPhrases[activeSignature];
        pool.push(...phrases, ...phrases, ...phrases);
      }
    }

    // Backend footer line (triple weight — it's the curated daily phrase)
    if (footerLine) {
      pool.push(footerLine, footerLine, footerLine);
    }

    // General maxi fallback (small amount)
    pool.push(PHRASE_LIBRARY.GENERAL_MAXI[Math.floor(Math.random() * PHRASE_LIBRARY.GENERAL_MAXI.length)]);
    pool.push(PHRASE_LIBRARY.GENERAL_MAXI[Math.floor(Math.random() * PHRASE_LIBRARY.GENERAL_MAXI.length)]);

    // Shuffle
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    poolRef.current = pool;
    indexRef.current = 0;
    setCurrentThought(pool[0] ?? '');
  }, [mood, blockState, networkState, footerLine, macroState, seasonState]);

  // Rotate thoughts every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const pool = poolRef.current;
      if (pool.length === 0) return;
      indexRef.current = (indexRef.current + 1) % pool.length;
      setCurrentThought(pool[indexRef.current]);
    }, 10_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full overflow-hidden flex items-center justify-center gap-1.5 py-1" style={{ minHeight: 24 }}>
      <ThoughtBubbleSvg />
      <AnimatePresence mode="wait">
        <motion.p
          key={currentThought}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="text-[12px] font-body font-semibold text-center italic truncate px-1"
          style={{ color: 'hsl(var(--foreground) / 0.6)' }}
        >
          {currentThought}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
